// Persistência da cena no room próprio, no lugar do Firestore da Excalidraw.
//
// Mantém EXATAMENTE a mesma superfície pública que `data/firebase.ts` exportava
// (6 funções), então Collab.tsx, App.tsx e o teste do repo não mudam. É por isso
// que o patch cabe em um arquivo: quando o upstream mexer no firebase.ts, o
// conflito aparece no shim e não espalhado pelo app.
//
// O que sai daqui já vai cifrado com a roomKey, que vive só no fragmento da URL.
// O servidor guarda bytes opacos.
import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";
import { toBrandedType } from "@excalidraw/common";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

// A persistência mora dentro do próprio room, então por padrão herda a URL dele.
const BASE = (
  import.meta.env.VITE_APP_STORAGE_URL ||
  import.meta.env.VITE_APP_WS_SERVER_URL ||
  ""
).replace(/\/+$/, "");

const MAX_TENTATIVAS = 5;

// btoa com spread estoura a pilha em cena grande; vai em blocos.
const paraBase64 = (bytes: Uint8Array): string => {
  let bin = "";
  const BLOCO = 0x8000;
  for (let i = 0; i < bytes.length; i += BLOCO) {
    bin += String.fromCharCode(...bytes.subarray(i, i + BLOCO));
  }
  return btoa(bin);
};

const deBase64 = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
};

type CenaGravada = {
  sceneVersion: number;
  ciphertext: string;
  iv: string;
};

const cifrarElementos = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const encoded = new TextEncoder().encode(JSON.stringify(elements));
  const { encryptedBuffer, iv } = await encryptData(key, encoded);
  return { ciphertext: encryptedBuffer, iv };
};

const decifrarElementos = async (
  cena: CenaGravada,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = deBase64(cena.ciphertext) as Uint8Array<ArrayBuffer>;
  const iv = deBase64(cena.iv) as Uint8Array<ArrayBuffer>;
  const decrypted = await decryptData(iv, ciphertext, roomKey);
  return JSON.parse(new TextDecoder("utf-8").decode(new Uint8Array(decrypted)));
};

const montarCena = async (
  elements: readonly SyncableExcalidrawElement[],
  roomKey: string,
): Promise<CenaGravada> => {
  const sceneVersion = getSceneVersion(elements);
  const { ciphertext, iv } = await cifrarElementos(roomKey, elements);
  return {
    sceneVersion,
    ciphertext: paraBase64(new Uint8Array(ciphertext)),
    iv: paraBase64(iv),
  };
};

const buscarCena = async (roomId: string): Promise<CenaGravada | null> => {
  const r = await fetch(`${BASE}/api/scene/${roomId}`);
  if (r.status === 404) {
    return null;
  }
  if (!r.ok) {
    throw new Error(`falha ao ler cena: ${r.status}`);
  }
  return (await r.json()) as CenaGravada;
};

class CacheVersaoCena {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => CacheVersaoCena.cache.get(socket);
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    CacheVersaoCena.cache.set(socket, getSceneVersion(elements));
  };
}

// -----------------------------------------------------------------------------

// Só existia para entregar o handle do Firebase Storage, e quem a chama é o
// "Export to Excalidraw+", que sobe a cena para o serviço pago da Excalidraw.
// Numa instância própria esse destino não existe, então a função falha com
// motivo legível em vez de estourar dentro do SDK do Firebase.
// Tipada como `any` porque o chamador a repassa para `ref()`, que espera um
// FirebaseStorage de verdade.
export const loadFirebaseStorage = async (): Promise<any> => {
  throw new Error(
    "Export to Excalidraw+ não funciona nesta instância: a cena fica no room próprio, não no Firebase da Excalidraw.",
  );
};

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    return CacheVersaoCena.get(portal.socket) === getSceneVersion(elements);
  }
  return true;
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (!roomId || !roomKey || !socket || isSavedToFirebase(portal, elements)) {
    return null;
  }

  // Substitui a transação do Firestore: lê, reconcilia, grava, e se alguém
  // gravou no meio (409) refaz a reconciliação com o que veio de volta.
  let paraGravar = elements;
  let gravada: CenaGravada | null = null;

  for (let tentativa = 0; tentativa < MAX_TENTATIVAS; tentativa++) {
    const atual = await buscarCena(roomId);

    if (atual) {
      const anteriores = getSyncableElements(
        restoreElements(await decifrarElementos(atual, roomKey), null),
      );
      paraGravar = getSyncableElements(
        reconcileElements(
          paraGravar,
          anteriores as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );
    }

    const cena = await montarCena(paraGravar, roomKey);
    const r = await fetch(`${BASE}/api/scene/${roomId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cena),
    });

    if (r.ok) {
      gravada = cena;
      break;
    }
    if (r.status !== 409) {
      throw new Error(`falha ao gravar cena: ${r.status}`);
    }
    // 409: o laço repete e reconcilia contra a versão que ganhou.
  }

  if (!gravada) {
    return null;
  }

  const guardados = getSyncableElements(
    restoreElements(await decifrarElementos(gravada, roomKey), null),
  );
  CacheVersaoCena.set(socket, guardados);
  return toBrandedType<RemoteExcalidrawElement[]>(guardados);
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const cena = await buscarCena(roomId);
  if (!cena) {
    return null;
  }
  const elements = getSyncableElements(
    restoreElements(await decifrarElementos(cena, roomKey), null, {
      deleteInvisibleElements: true,
    }),
  );
  if (socket) {
    CacheVersaoCena.set(socket, elements);
  }
  return elements;
};

const limparPrefixo = (prefix: string) => prefix.replace(/^\/+|\/+$/g, "");

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];
  const base = limparPrefixo(prefix);

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const r = await fetch(`${BASE}/api/files/${base}/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream" },
          body: new Blob([buffer as BlobPart]),
        });
        if (!r.ok) {
          throw new Error(String(r.status));
        }
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();
  const base = limparPrefixo(prefix);

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(`${BASE}/api/files/${base}/${id}`);
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();
          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            { decryptionKey },
          );
          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL: new TextDecoder().decode(data) as DataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
